import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function LeadActivities() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [activities, setActivities] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [tab, setTab] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [activityTypes, setActivityTypes] = useState([]);
  const [page, setPage] = useState(1);
  const [myActivities, setMyActivities] = useState({ today: [], overdue: [], upcoming: [] });
  const [showCreate, setShowCreate] = useState(false);
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState({ lead_id: '', activity_type_id: '', subject: '', description: '', scheduled_at: '', priority: 'medium', is_follow_up: false });

  const fetchActivities = useCallback(() => {
    if (!companyId || companyId === 'holding') return;
    setLoading(true);
    const params = new URLSearchParams({ page, per_page: 25 });
    if (statusFilter) params.append('status', statusFilter);
    if (typeFilter) params.append('activity_type_id', typeFilter);

    fetch(`${API}/companies/${companyId}/leads/activities?${params}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        setActivities(Array.isArray(data.data) ? data.data : []);
        setPagination({ current_page: data.current_page, last_page: data.last_page, total: data.total });
      })
      .catch(() => setActivities([]))
      .finally(() => setLoading(false));
  }, [companyId, page, statusFilter, typeFilter]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  useEffect(() => {
    if (!companyId || companyId === 'holding') return;
    fetch(`${API}/companies/${companyId}/leads/settings/activity-types`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(d => setActivityTypes(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/companies/${companyId}/leads/activities/my-activities`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : {}).then(d => setMyActivities({ today: Array.isArray(d.today) ? d.today : [], overdue: Array.isArray(d.overdue) ? d.overdue : [], upcoming: Array.isArray(d.upcoming) ? d.upcoming : [] })).catch(() => {});
    fetch(`${API}/companies/${companyId}/leads?per_page=200`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] }).then(d => setLeads(Array.isArray(d.data) ? d.data : [])).catch(() => {});
  }, [companyId]);

  const createActivity = () => {
    fetch(`${API}/companies/${companyId}/leads/activities`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => { setShowCreate(false); setForm({ lead_id: '', activity_type_id: '', subject: '', description: '', scheduled_at: '', priority: 'medium', is_follow_up: false }); fetchActivities(); })
      .catch(() => window.alert('Failed to create activity'));
  };

  const completeActivity = (id) => {
    fetch(`${API}/companies/${companyId}/leads/activities/${id}/complete`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ outcome: 'completed' }),
    }).then(r => r.ok ? r.json() : null).then(d => { if (d) fetchActivities(); });
  };

  const cancelActivity = (id) => {
    if (!window.confirm('Cancel this activity?')) return;
    fetch(`${API}/companies/${companyId}/leads/activities/${id}/cancel`, {
      method: 'POST', headers: getAuthHeaders(),
    }).then(r => r.ok ? r.json() : null).then(d => { if (d) fetchActivities(); });
  };

  if (!companyId || companyId === 'holding') return <div style={{ padding: 32 }}><h2>Select a company</h2></div>;

  const statusColors = { scheduled: '#3b82f6', in_progress: '#f59e0b', completed: '#22c55e', cancelled: '#9ca3af', overdue: '#ef4444' };
  const priorityColors = { high: '#ef4444', medium: '#f59e0b', low: '#6b7280' };
  const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };
  const tabStyle = (t) => ({ padding: '10px 20px', border: 'none', borderBottom: tab === t ? '3px solid #6366f1' : '3px solid transparent', background: 'none', fontWeight: tab === t ? 700 : 400, color: tab === t ? '#6366f1' : '#6b7280', cursor: 'pointer', fontSize: 14 });

  const renderActivityCard = (a, showActions = true) => (
    <div key={a.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 10, borderLeft: `4px solid ${statusColors[a.status] || '#6b7280'}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {a.is_follow_up && <span style={{ fontSize: 11, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 8, marginRight: 6 }}>Follow-Up</span>}
            {a.subject}
          </div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            {a.activity_type?.name || 'Activity'} • {a.lead?.lead_name || 'Unknown Lead'}
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
            Scheduled: {a.scheduled_at ? new Date(a.scheduled_at).toLocaleString() : '—'}
            {a.completed_at && <span> • Completed: {new Date(a.completed_at).toLocaleString()}</span>}
          </div>
          {a.description && <div style={{ fontSize: 13, marginTop: 6, color: '#374151' }}>{a.description}</div>}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, color: '#fff', background: priorityColors[a.priority] || '#6b7280' }}>{a.priority}</span>
          <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, color: '#fff', background: statusColors[a.status] || '#6b7280' }}>{a.status}</span>
        </div>
      </div>
      {showActions && a.status !== 'completed' && a.status !== 'cancelled' && (
        <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
          <button onClick={() => completeActivity(a.id)} style={{ fontSize: 12, padding: '4px 12px', border: '1px solid #22c55e', background: '#f0fdf4', color: '#16a34a', borderRadius: 4, cursor: 'pointer' }}>✓ Complete</button>
          <button onClick={() => cancelActivity(a.id)} style={{ fontSize: 12, padding: '4px 12px', border: '1px solid #ef4444', background: '#fef2f2', color: '#dc2626', borderRadius: 4, cursor: 'pointer' }}>✗ Cancel</button>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>Activities & Follow-Ups</h2>
        <button onClick={() => setShowCreate(true)} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer' }}>+ New Activity</button>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid #e5e7eb', marginBottom: 20, display: 'flex', gap: 4 }}>
        {['all', 'my', 'overdue'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={tabStyle(t)}>{t === 'all' ? 'All Activities' : t === 'my' ? 'My Activities' : 'Overdue'}</button>
        ))}
      </div>

      {/* My Activities Dashboard */}
      {tab === 'my' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 24 }}>
          <div>
            <h4 style={{ fontWeight: 600, color: '#3b82f6', marginBottom: 12 }}>Today ({myActivities.today.length})</h4>
            {myActivities.today.map(a => renderActivityCard(a))}
            {myActivities.today.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>No activities today</div>}
          </div>
          <div>
            <h4 style={{ fontWeight: 600, color: '#ef4444', marginBottom: 12 }}>Overdue ({myActivities.overdue.length})</h4>
            {myActivities.overdue.map(a => renderActivityCard(a))}
            {myActivities.overdue.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>None overdue</div>}
          </div>
          <div>
            <h4 style={{ fontWeight: 600, color: '#f59e0b', marginBottom: 12 }}>Upcoming ({myActivities.upcoming.length})</h4>
            {myActivities.upcoming.map(a => renderActivityCard(a))}
            {myActivities.upcoming.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>No upcoming</div>}
          </div>
        </div>
      )}

      {/* All Activities / Overdue */}
      {(tab === 'all' || tab === 'overdue') && (
        <>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={{ ...inputStyle, maxWidth: 160 }}>
              <option value="">All Statuses</option>
              {['scheduled', 'in_progress', 'completed', 'cancelled'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
            <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }} style={{ ...inputStyle, maxWidth: 180 }}>
              <option value="">All Types</option>
              {activityTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          <div style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}>
            {initialLoading ? <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</div>
              : activities.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No activities found</div>
              : activities
                  .filter(a => tab === 'overdue' ? (a.status === 'scheduled' || a.status === 'in_progress') && a.scheduled_at && new Date(a.scheduled_at) < new Date() : true)
                  .map(a => renderActivityCard(a))
            }
          </div>

          {/* Pagination */}
          {pagination.last_page > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              {Array.from({ length: pagination.last_page }, (_, i) => (
                <button key={i} onClick={() => setPage(i + 1)}
                  style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: page === i + 1 ? '#6366f1' : '#fff', color: page === i + 1 ? '#fff' : '#374151', cursor: 'pointer', fontWeight: page === i + 1 ? 600 : 400 }}>
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 520, maxHeight: '85vh', overflowY: 'auto' }}>
            <h3 style={{ marginBottom: 20 }}>New Activity</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ gridColumn: '1/-1' }}><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Lead *</label>
                <select style={inputStyle} value={form.lead_id} onChange={e => setForm({ ...form, lead_id: e.target.value })}>
                  <option value="">Select lead</option>{leads.map(l => <option key={l.id} value={l.id}>{l.lead_name}</option>)}
                </select></div>
              <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Type *</label>
                <select style={inputStyle} value={form.activity_type_id} onChange={e => setForm({ ...form, activity_type_id: e.target.value })}>
                  <option value="">Select type</option>{activityTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select></div>
              <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Priority</label>
                <select style={inputStyle} value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                  <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                </select></div>
              <div style={{ gridColumn: '1/-1' }}><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Subject *</label><input style={inputStyle} value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} /></div>
              <div style={{ gridColumn: '1/-1' }}><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Description</label><textarea style={{ ...inputStyle, minHeight: 70 }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
              <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Scheduled At *</label><input style={inputStyle} type="datetime-local" value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} /></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 20 }}>
                <input type="checkbox" checked={form.is_follow_up} onChange={e => setForm({ ...form, is_follow_up: e.target.checked })} />
                <label style={{ fontSize: 13, fontWeight: 600 }}>Follow-Up</label>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowCreate(false)} style={{ padding: '8px 20px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={createActivity} disabled={!form.lead_id || !form.subject || !form.scheduled_at}
                style={{ padding: '8px 20px', border: 'none', borderRadius: 6, background: '#6366f1', color: '#fff', fontWeight: 600, cursor: 'pointer', opacity: form.lead_id && form.subject && form.scheduled_at ? 1 : 0.5 }}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
